//Google Identity Services（GIS）—— 原生 Google 登入
//
//  不使用 Firebase Auth：少一層依賴，也少一份要維護的服務設定。
//  流程：載入 Google 的 script → 渲染官方登入按鈕 → 使用者登入後
//        callback 拿到 credential（Google 簽發的 ID Token）→ 交給後端驗證。
//
//  按鈕一定要用 google.accounts.id.renderButton 渲染官方按鈕，
//  不能自己畫一顆假的 —— 這是 Google 品牌規範的要求，
//  而且官方按鈕會自動處理多語系與各種登入狀態。

const GIS_SRC = "https://accounts.google.com/gsi/client";

export const getClientId = () => process.env.REACT_APP_GOOGLE_CLIENT_ID || "";
export const isGoogleConfigured = () => !!getClientId();

//載入 GIS script
//  快取 Promise 而不是只用旗標：同時有兩處要用時，兩邊都會等到同一次載入完成，
//  不會重複插入 script，也不會有人拿到「還沒載好」的狀態。
let loadPromise = null;

export const loadGoogleIdentity = () => {
  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve, reject) => {
    if (!isGoogleConfigured()) {
      reject(new Error("尚未設定 REACT_APP_GOOGLE_CLIENT_ID"));
      return;
    }

    //已經載過了（例如 hot reload 後模組重跑）
    if (window.google?.accounts?.id) {
      resolve(window.google.accounts.id);
      return;
    }

    const existing = document.querySelector(`script[src="${GIS_SRC}"]`);
    const script = existing || document.createElement("script");

    script.onload = () => {
      if (window.google?.accounts?.id) resolve(window.google.accounts.id);
      else reject(new Error("Google 登入元件載入異常"));
    };
    //網路被擋（例如中國網路環境或廣告阻擋外掛）時要有明確錯誤，
    //不然畫面上只會是一顆永遠不出現的按鈕
    script.onerror = () => {
      loadPromise = null; //允許之後重試
      reject(new Error("無法載入 Google 登入元件，請檢查網路或廣告阻擋設定"));
    };

    if (!existing) {
      script.src = GIS_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  });

  return loadPromise;
};

//在指定元素內渲染官方 Google 登入按鈕
//  container：DOM 元素
//  onCredential：登入成功，帶回 ID Token 字串
//  width：按鈕寬度（px）。GIS 上限 400，超過會被忽略而變成預設寬度
export const renderGoogleButton = async (container, { onCredential, width }) => {
  const gid = await loadGoogleIdentity();

  gid.initialize({
    client_id: getClientId(),
    callback: (response) => {
      if (response?.credential) onCredential(response.credential);
    },
    //關掉自動選取：使用者按過登出後不該一進站就被自動登回去
    auto_select: false,
    //點按鈕以外的地方不要取消流程 —— 手機上很容易誤觸
    cancel_on_tap_outside: false,
  });

  gid.renderButton(container, {
    type: "standard",
    //白底：深色主題上最顯眼，也是使用者最熟悉的樣子
    theme: "outline",
    size: "large",
    shape: "pill",
    text: "continue_with",
    logo_alignment: "center",
    locale: "zh_TW",
    width: Math.min(width || 320, 400),
  });
};

//登出
//  GIS 沒有真正的「登出」—— 它不維護登入狀態，session 在我們自己的 cookie。
//  disableAutoSelect 是必要的：不呼叫的話，下次進站會自動用同一個帳號登回去，
//  使用者會覺得「登出沒有用」。
export const googleSignOut = () => {
  try {
    window.google?.accounts?.id?.disableAutoSelect();
  } catch (e) {
    //Google 元件沒載入過就不用處理，登出不該因此失敗
  }
};
